import { useEffect, useState } from 'react';

import { usePlaygroundStore } from '@/Pages/Playground/PlaygroundHome/hooks/usePlaygroundStore';
import { useSessionNonStream } from '@/hooks/useSwr';

// This code is for the continual polling of feedback while a playground experiment is running.

const countValues = (values: Record<string, number>) => {
  return Object.values(values).reduce((acc, curr) => acc + curr, 0);
};
export const usePollSessionForFeedback = (
  sessionId?: string,
  expectedFeedbackKeys?: string[]
) => {
  const [isSessionFinished, setIsSessionFinished] = useState(false);
  const expectedNumberOfRuns = usePlaygroundStore(
    (state) => state.expectedNumberOfRuns
  );

  const { data: session } = useSessionNonStream(
    sessionId ?? null,
    {
      include_stats: true,
    },
    {
      refreshInterval: isSessionFinished ? undefined : 1000,
      keepPreviousData: false,
    }
  );

  useEffect(() => {
    setIsSessionFinished(false);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      return;
    }
    // if we don't have evaluators we don't care about feedback stats
    const hasEvaluators = (expectedFeedbackKeys?.length ?? 0) > 0;
    if (!hasEvaluators) {
      setIsSessionFinished(true);
      return;
    }
    // an indirect way to determine stopping condition for polling session
    // we check that all feedback stats have the same n value as the run count (accounts for repetitions)
    const hasFeedbackStats =
      session?.feedback_stats && Object.keys(session.feedback_stats).length > 0;

    if (!hasFeedbackStats || !session.run_count) {
      return;
    }

    const allMatch = !!expectedFeedbackKeys?.every((key) => {
      const stat = session.feedback_stats?.[key];
      if (!stat) {
        return false;
      }
      return (
        stat.n + (stat.errors ?? 0) === session.run_count ||
        countValues(stat.values ?? {}) + (stat.errors ?? 0) ===
          expectedNumberOfRuns
      );
    });

    setIsSessionFinished(allMatch && !!session.end_time);
  }, [
    session,
    expectedFeedbackKeys,
    isSessionFinished,
    sessionId,
    expectedNumberOfRuns,
  ]);

  return { session, isSessionFinished };
};
